import { useEffect, useRef } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENTS, MERCHANT } from '@neke/core';
import { Header } from './components/Header';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Onboarding } from './pages/Onboarding';
import { Test } from './pages/Test';
import { Result } from './pages/Result';
import { PairReport } from './pages/PairReport';
import { Payment } from './pages/Payment';
import { Matches } from './pages/Matches';
import { Invites } from './pages/Invites';
import { Admin } from './pages/Admin';
import { AdminAudit } from './pages/AdminAudit';
import { AdminFunnel } from './pages/AdminFunnel';
import { About, Blog, HowItWorks, NotFound, Spheres } from './pages/Static';
import { Contacts, Offer, Privacy, Refund } from './pages/Legal';
import { useSession } from './store/session';
import { routeOf, track, trackPageView } from './analytics';
import { trackGtagPageView } from './gtag';

export default function App() {
  const { t } = useTranslation();
  const restore = useSession((s) => s.restore);
  const location = useLocation();

  // Сессию восстанавливает только App, и только здесь. Раньше то же самое делал
  // ещё и AuthCallback, и на возврате от Google обе попытки уходили в
  // /api/auth/refresh с одной свежей кукой: сервер её ротирует, вторая приходила
  // с уже отозванной и получала «кража токена» — человека выбрасывало из
  // аккаунта сразу после входа. AuthCallback теперь просто ждёт результат.
  useEffect(() => {
    void restore();
  }, [restore]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    trackPageView(location.pathname);
    // GA сам просмотры не шлёт: у него выключен send_page_view, чтобы токен
    // приглашения из адреса не уехал в отчёты. См. `gtag.ts`.
    trackGtagPageView(location.pathname);
  }, [location.pathname]);

  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/test" element={<Test />} />
        <Route path="/result" element={<Result />} />
        <Route path="/pair/id/:pairId" element={<PairReport />} />
        <Route path="/pair/:token" element={<PairReport />} />
        <Route path="/payment/:id" element={<Payment />} />
        <Route path="/matches" element={<Matches />} />
        {/* Вход только с /result: в шапку не выносим, чтобы не разрасталась навигация. */}
        <Route path="/invites" element={<Invites />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/log" element={<AdminAudit />} />
        <Route path="/admin/funnel" element={<AdminFunnel />} />
        <Route path="/about" element={<About />} />
        <Route path="/how" element={<HowItWorks />} />
        <Route path="/spheres" element={<Spheres />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/offer" element={<Offer />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      <footer className="site">
        <p className="fine">{t('footer.disclaimer')}</p>
        <p className="fine">
          {t('footer.privacy')}
        </p>
        {/* Модерация платёжных систем ищет эти ссылки именно в подвале. */}
        <nav className="legal-links">
          <Link to="/offer">{t('legal.offer')}</Link>
          <Link to="/refund">{t('legal.refund')}</Link>
          <Link to="/privacy">{t('legal.privacy')}</Link>
          <Link to="/contacts">{t('legal.contacts')}</Link>
        </nav>
        <p className="fine">
          {MERCHANT.name} · {t('contacts.bin')} {MERCHANT.bin}
        </p>
      </footer>
    </>
  );
}

/**
 * Google возвращает нас сюда: refresh-кука уже стоит, меняем её на токен.
 *
 * Меняет её App — он зовёт restore() на старте, в том числе и на этом адресе.
 * Здесь только ждём итог по `ready` и уводим человека дальше: второй поход в
 * /api/auth/refresh с той же кукой сервер счёл бы кражей токена.
 */
function AuthCallback() {
  const ready = useSession((s) => s.ready);
  const { t } = useTranslation();
  // Уводим ровно один раз: в StrictMode эффекты прогоняются дважды, а событие
  // входа и переход — вещи одноразовые.
  const left = useRef(false);

  useEffect(() => {
    if (!ready || left.current) return;
    left.current = true;

    const user = useSession.getState().user;
    const next = new URLSearchParams(window.location.search).get('next') ?? '/result';
    // Пол и согласие Google не отдаёт — без них дальше идти некуда.
    const needsOnboarding = user !== null && (!user.sex || !user.hasConsent);
    const target = needsOnboarding ? `/onboarding?next=${encodeURIComponent(next)}` : next;
    // Ждём отправку: после replace страница уже не успеет ничего послать.
    void track(ANALYTICS_EVENTS.signedIn, {
      method: 'google',
      success: Boolean(user),
      needs_onboarding: needsOnboarding,
      next_route: routeOf(next),
    }).finally(() => window.location.replace(target));
  }, [ready]);

  return (
    <div className="page">
      <p className="muted">{t('common.loading')}</p>
    </div>
  );
}
